/* Bulltong — The Herd · product page: variants, gallery, sticky purchase bar. */
(() => {
  'use strict';

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const SOLD_OUT_NOTE = 'This size is sold out right now.';

  function initProduct(root) {
    if (!root || root.dataset.ready) return;
    let data;
    try { data = JSON.parse(root.querySelector('[data-product-json]').textContent); } catch (_) { return; }
    root.dataset.ready = 'true';
    const life = new AbortController();
    const observers = [];
    let enterFrame = 0;

    const q = (selector) => root.querySelector(selector);
    const qa = (selector) => Array.from(root.querySelectorAll(selector));
    const variantInputs = qa('[data-variant-input]');
    const optionGroups = qa('[data-option-group]');
    const select = q('[data-variant-select]');
    const addButtons = qa('[data-add-button]');
    const track = q('[data-gallery-track]');
    const slides = qa('[data-gallery-slide]');
    const thumbs = qa('[data-gallery-thumb]');
    const stageImages = qa('[data-stage-size]');

    const currentVariant = () => {
      if (variantInputs.length) {
        const checked = variantInputs.find((input) => input.checked);
        return data.variants.find((variant) => String(variant.id) === checked?.value);
      }
      if (optionGroups.length) {
        const values = optionGroups.map((group) => group.querySelector('input:checked')?.value);
        return data.variants.find((variant) => variant.options.every((option, i) => option === values[i]));
      }
      return data.variants[0];
    };

    /* Gallery */
    const setThumb = (index) => thumbs.forEach((thumb, n) => thumb.setAttribute('aria-current', n === index ? 'true' : 'false'));
    const goTo = (id, smooth = true) => {
      const index = slides.findIndex((slide) => slide.dataset.gallerySlide === String(id));
      if (index < 0 || !track) return;
      track.scrollTo({ left: index * track.clientWidth, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto' });
      setThumb(index);
    };
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(thumb.dataset.galleryThumb), { signal: life.signal }));
    if (track && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) setThumb(slides.indexOf(entry.target));
        });
      }, { root: track, threshold: [0.6] });
      slides.forEach((slide) => observer.observe(slide));
      observers.push(observer);
      track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const current = Math.round(track.scrollLeft / track.clientWidth);
        const next = Math.min(slides.length - 1, Math.max(0, current + (event.key === 'ArrowRight' ? 1 : -1)));
        goTo(slides[next].dataset.gallerySlide);
      }, { signal: life.signal });
    }

    /* Variant state */
    const setText = (selector, text) => qa(selector).forEach((element) => { element.textContent = text; });

    function update(variant, updateUrl = true) {
      if (!variant) {
        addButtons.forEach((button) => { button.disabled = true; button.dataset.soldOut = 'true'; });
        qa('[data-payment-wrap]').forEach((wrap) => { wrap.hidden = true; });
        setText('[data-add-label]', 'Unavailable');
        setText('[data-add-short]', 'Unavailable');
        setText('[data-availability]', 'That combination isn’t available.');
        return;
      }
      if (select) select.value = variant.id;
      setText('[data-price]', variant.price);
      const compare = q('[data-compare]');
      if (compare) { compare.hidden = !variant.compare; compare.textContent = variant.compare || ''; }
      const save = q('[data-save]');
      if (save) { save.hidden = !variant.save; save.textContent = `Save ${variant.save}%`; }
      addButtons.forEach((button) => { button.disabled = !variant.available; button.dataset.soldOut = String(!variant.available); });
      qa('[data-payment-wrap]').forEach((wrap) => { wrap.hidden = !variant.available; });
      setText('[data-add-label]', variant.available ? 'Add to bag' : 'Sold out');
      setText('[data-add-short]', variant.available ? 'Add to bag' : 'Sold out');
      setText('[data-availability]', variant.available ? '' : SOLD_OUT_NOTE);
      setText('[data-stage-caption]', variant.label);
      setText('[data-bar-size]', variant.label);

      const proof = data.proof && data.proof[variant.size];
      if (proof) {
        setText('[data-proof-protein]', proof[0]);
        setText('[data-proof-calories]', proof[1]);
        setText('[data-proof-note]', `Per ${variant.size}g pack${data.boxed ? ' in the box' : ''}, as published on the pack.`);
      }

      if (data.stage && stageImages.length) {
        stageImages.forEach((image) => {
          const show = image.dataset.stageSize === variant.size;
          if (show && image.hidden) {
            image.hidden = false;
            image.loading = 'eager';
            if (!reducedMotion.matches) {
              image.classList.remove('is-entering');
              if (enterFrame) cancelAnimationFrame(enterFrame);
              enterFrame = requestAnimationFrame(() => {
                enterFrame = requestAnimationFrame(() => { enterFrame = 0; image.classList.add('is-entering'); });
              });
            }
          } else if (!show) {
            image.hidden = true;
          }
        });
        goTo('stage');
      } else if (variant.media) {
        goTo(variant.media);
      }

      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url);
      }
      root.dispatchEvent(new CustomEvent('product:variant-change', { bubbles: true, detail: { variant } }));
    }

    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-variant-input], [data-option-input]')) update(currentVariant());
    }, { signal: life.signal });
    update(currentVariant(), false);

    /* Sticky purchase bar: use a cached boundary so fast scroll jumps cannot skip it. */
    const bar = q('[data-sticky-bar]');
    const buyRow = q('.pdp__buy');
    let barFrame = 0;
    if (bar && buyRow) {
      let buyBottom = 0;
      const barButton = bar.querySelector('button');
      const syncBar = () => {
        barFrame = 0;
        const visible = window.scrollY > buyBottom;
        if (bar.classList.contains('is-visible') === visible) return;
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
        if (barButton) barButton.tabIndex = visible ? 0 : -1;
      };
      const scheduleBar = () => {
        if (!barFrame) barFrame = requestAnimationFrame(syncBar);
      };
      const measureBar = () => {
        buyBottom = buyRow.getBoundingClientRect().bottom + window.scrollY;
        scheduleBar();
      };
      measureBar();
      window.addEventListener('scroll', scheduleBar, { passive: true, signal: life.signal });
      window.addEventListener('resize', measureBar, { passive: true, signal: life.signal });
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(measureBar);
        observer.observe(buyRow);
        observer.observe(root);
        observers.push(observer);
      }
    }

    /* Pause the idle pack motion when the stage is off screen. */
    const stage = q('.pdp__media');
    if (stage && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => root.classList.toggle('is-paused', !entry.isIntersecting));
      observer.observe(stage);
      observers.push(observer);
    }
    root.bulltongDispose = () => {
      life.abort();
      observers.forEach((observer) => observer.disconnect());
      if (enterFrame) cancelAnimationFrame(enterFrame);
      if (barFrame) cancelAnimationFrame(barFrame);
      delete root.dataset.ready;
    };
  }

  document.querySelectorAll('[data-product]').forEach(initProduct);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-product]').forEach(initProduct));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-product]').forEach((product) => product.bulltongDispose?.()));
})();
